import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Per-user, per-board column width storage + resize handles.
 *
 * Mirrors `useHiddenColumns` so the two concerns stay decoupled:
 *  - Persists `{ [colKey]: widthInPx }` in localStorage scoped to user+board.
 *  - Injects a `<style>` block giving `.col-{key} { width / min-width: Xpx }`
 *    so both `<th class="col-x">` and `<td class="col-x">` track together.
 *  - Imperatively attaches a 6px drag handle on the right edge of every
 *    `<th class="col-*">` inside the board's `<table data-board-table="{board}">`.
 *    Drag = resize live. Double-click = clear that column's stored width.
 *
 * Why imperative DOM rather than React components? Each project board
 * already hand-codes ~15 `<th>` elements; adding a wrapper component to
 * every one would be a sprawling refactor and risks regressing the
 * carefully-tuned `col-*` classNames the hide-column feature also relies
 * on. Scanning once after mount keeps the change minimally invasive.
 */
export function useColumnWidths(board, userEmail) {
  const key = `restorepm:column-widths:${userEmail || 'anon'}:${board}`;

  const readForKey = (k) => {
    try {
      const raw = localStorage.getItem(k);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch { /* ignore */ }
    return {};
  };

  const [widths, setWidths] = useState(() => readForKey(key));

  // Re-read whenever the storage key changes (e.g. user email resolves).
  useEffect(() => {
    setWidths(readForKey(key));
  }, [key]);

  // Persist only after explicit user actions.
  const userActionRef = useRef(false);
  useEffect(() => {
    if (!userActionRef.current) return;
    userActionRef.current = false;
    try { localStorage.setItem(key, JSON.stringify(widths)); }
    catch { /* ignore */ }
  }, [key, widths]);

  // Inject the width stylesheet for this board.
  useEffect(() => {
    const styleId = `restorepm-col-widths-${board}`;
    let el = document.getElementById(styleId);
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      el.setAttribute('data-board', board);
      document.head.appendChild(el);
    }
    const rules = Object.entries(widths)
      .filter(([, w]) => Number.isFinite(w) && w > 0)
      .map(([k, w]) => `[data-board-table="${board}"] .col-${k} { width: ${w}px !important; min-width: ${w}px !important; max-width: ${w}px !important; }`);
    el.textContent = rules.join('\n');
    return () => {
      const cur = document.getElementById(styleId);
      if (cur) cur.remove();
    };
  }, [board, widths]);

  // Stable refs so the DOM listeners never need re-binding.
  const setWidthRef = useRef(null);
  const clearWidthRef = useRef(null);
  setWidthRef.current = (colKey, px) => {
    userActionRef.current = true;
    setWidths((prev) => ({ ...prev, [colKey]: Math.round(px) }));
  };
  clearWidthRef.current = (colKey) => {
    userActionRef.current = true;
    setWidths((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  // Attach resize handles. Runs once per board mount. Because each board
  // conditionally renders its <table> (loading -> "No projects" -> table),
  // we may mount before the table exists, so we watch document.body until
  // it appears, then switch to a narrow MutationObserver on <thead> so
  // handles re-attach after the toggle/hide feature rewrites the header.
  useEffect(() => {
    const HANDLE_CLASS = 'restorepm-col-resizer';
    let theadObserver = null;

    const attach = (table) => {
      const ths = table.querySelectorAll('thead th[class*="col-"]');
      ths.forEach((th) => {
        const cls = th.className.match(/(?:^|\s)col-([A-Za-z0-9_]+)/);
        if (!cls) return;
        const colKey = cls[1];

        if (getComputedStyle(th).position === 'static') {
          th.style.position = 'relative';
        }
        if (th.querySelector(`.${HANDLE_CLASS}`)) return; // already wired

        const handle = document.createElement('div');
        handle.className = HANDLE_CLASS;
        handle.setAttribute('data-testid', `col-resize-${board}-${colKey}`);
        handle.title = 'Drag to resize. Double-click to reset.';
        handle.style.cssText = [
          'position:absolute',
          'top:0',
          'right:-3px',
          'width:6px',
          'height:100%',
          'cursor:col-resize',
          'user-select:none',
          'touch-action:none',
          'z-index:5',
        ].join(';');

        const onDown = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const startX = ev.clientX;
          const startW = th.getBoundingClientRect().width;
          let raf = 0;
          let pending = startW;
          const apply = () => {
            raf = 0;
            if (setWidthRef.current) setWidthRef.current(colKey, pending);
          };
          const onMove = (e) => {
            const delta = e.clientX - startX;
            pending = Math.max(40, startW + delta);
            if (!raf) raf = requestAnimationFrame(apply);
          };
          const onUp = () => {
            if (raf) { cancelAnimationFrame(raf); apply(); }
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
          };
          document.body.style.cursor = 'col-resize';
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
        };
        const onDbl = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (clearWidthRef.current) clearWidthRef.current(colKey);
        };

        handle.addEventListener('mousedown', onDown);
        handle.addEventListener('dblclick', onDbl);
        // Swallow the synthetic click the browser fires on the parent <th>
        // at the end of a drag — otherwise mouseup bubbles up and triggers
        // the column-sort `onClick` even though we already stopped the
        // mousedown's propagation.
        handle.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        });
        th.appendChild(handle);
      });
    };

    const ensure = () => {
      const table = document.querySelector(`[data-board-table="${board}"]`);
      if (!table) return false;
      attach(table);
      if (!theadObserver) {
        const thead = table.querySelector('thead');
        if (thead) {
          theadObserver = new MutationObserver(() => attach(table));
          theadObserver.observe(thead, { childList: true, subtree: true });
        }
      }
      return true;
    };

    let bodyObserver = null;
    if (!ensure()) {
      bodyObserver = new MutationObserver(() => {
        if (ensure()) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      if (bodyObserver) bodyObserver.disconnect();
      if (theadObserver) theadObserver.disconnect();
      const table = document.querySelector(`[data-board-table="${board}"]`);
      if (table) table.querySelectorAll(`.${HANDLE_CLASS}`).forEach((n) => n.remove());
    };
  }, [board]);

  const resetAll = useCallback(() => {
    userActionRef.current = true;
    setWidths({});
  }, []);

  return { widths, resetAll };
}

export default useColumnWidths;
